import { isEmpty } from 'lodash';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { useReportError } from '@/hooks/useReportError';
import { RequeryDocumentRepresentationListDocuments } from '@/modules/files/DocumentRepresentation/DocumentRepresentationList';
import {
  FileUploaderV2,
  FileUploaderV2Props,
} from '@/modules/files/FileUploaderV2';
import { acceptedFileTypes } from '@/modules/files/FileUploaderV2/FileUploader.utils';
import { UploadedFileData } from '@/modules/files/FileUploaderV2/hooks/useUploadFile';
import { UploadFileV2Mutation } from '@/modules/files/graphql/FileUploaderV2.generated';
import { CreateDocumentInput, FileContext } from '@/types/schema';

import {
  getNewFileUploadFromUploadResult,
  NormalizedUploadedFileData,
} from '../documentUploaderUtils';
import { useCreateDocumentMutation } from '../graphql/TrustDocuments.generated';

export interface DocumentUploaderProps {
  showExistingDocuments?: boolean;
  variant?: FileUploaderV2Props<unknown>['variant'];
  shouldTriggerRefetch?: boolean;
  householdId: string;
  onDocumentUploadStarted?: () => void;
  onDocumentUploadSuccess?: ({
    documentId,
    fileId,
    documentName,
  }: {
    documentId: string;
    fileId: string;
    documentName: string;
  }) => void;
  onDocumentUploadError?: (error: Error) => void;
  errorMessage?: string;
  getCreateDocumentInput: (
    newFile: NormalizedUploadedFileData
  ) => CreateDocumentInput;
  disabled?: boolean;
  acceptTypes?: Partial<typeof acceptedFileTypes>;
}

export function DocumentUploader({
  shouldTriggerRefetch = true,
  onDocumentUploadStarted,
  onDocumentUploadError,
  onDocumentUploadSuccess,
  getCreateDocumentInput,
  householdId,
  disabled = false,
  variant = 'expanded',
  showExistingDocuments = false,
  acceptTypes,
}: DocumentUploaderProps) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const { reportError } = useReportError();
  const [createEntityDocument] = useCreateDocumentMutation();

  async function handleNewDocumentUpload(
    uploaded: UploadedFileData<UploadFileV2Mutation>[]
  ) {
    if (isEmpty(uploaded)) {
      return;
    }

    // We're only handling one file at a time for now.
    const { file, result } = uploaded[0]!;

    try {
      const normalizedFile = getNewFileUploadFromUploadResult(
        file,
        result?.data,
        householdId
      );
      if (!normalizedFile) {
        return;
      }

      const input = getCreateDocumentInput(normalizedFile);
      await createEntityDocument({
        variables: {
          input,
        },
        refetchQueries: shouldTriggerRefetch
          ? [RequeryDocumentRepresentationListDocuments]
          : [],
        onCompleted: (data) =>
          onDocumentUploadSuccess?.({
            documentId: data.createDocument.id,
            fileId: normalizedFile.fileId,
            documentName: data.createDocument.name,
          }),
      });
    } catch (err) {
      reportError('Failed to create document', err as Error);
      onDocumentUploadError && onDocumentUploadError(err as Error);
    }
  }

  return (
    <FileUploaderV2
      householdId={householdId}
      onUploadComplete={handleNewDocumentUpload}
      onUploadStarted={onDocumentUploadStarted}
      variant={variant}
      disabled={disabled || disabledFromContext}
      showExistingDocuments={showExistingDocuments}
      context={FileContext.Household}
      acceptTypes={acceptTypes}
    />
  );
}
